import classNames from "classnames";
import { useFormContext, useWatch } from "react-hook-form";
import { useMutation } from "react-query";
import { t } from "@/i18n-js/instance";
import { reactQueryPost } from "@/react/helpers/backendRequestHelpers";
import { activeStorageBlobUrl, internalApi } from "@/react/helpers/urlHelpers";
import type { CoursesLesson } from "@/react/types/CoursesLesson";
import { FormTipTapBlockEditor } from "@circle-react-uikit/Form/FormTipTapBlockEditor/FormTipTapBlockEditor";
import { useToast } from "@circle-react-uikit/ToastV2";
import { EmbedLoading } from "./EmbedLoading";
import { EmbedPreview } from "./EmbedPreview";
import { MediaPicker } from "./MediaPicker";
import { useMediaPickerModal } from "./MediaPickerModal";
import { MediaWithPlayback } from "./MediaWithPlayback";

interface LessonContentProps {
  lesson: CoursesLesson;
}

export const LessonContent = ({ lesson }: LessonContentProps) => {
  const { setValue } = useFormContext();
  const toast = useToast();
  const mediaPickerModal = useMediaPickerModal();
  const name = useWatch({ name: "name" });
  const featuredMedia = useWatch({ name: "featured_media" });
  const featuredMediaEmbed = useWatch({ name: "featured_media_embed" });
  const isFeaturedMediaEnabled = useWatch({ name: "featured_media_enabled" });
  const hasPlaybackUrl = Boolean(featuredMedia?.playback_url);
  const hasEmbeddedMedia = Boolean(featuredMediaEmbed?.html);
  const isAudioMode = !!featuredMedia?.content_type?.match("audio.*");

  const createEmbedMutation = useMutation(
    embedUrl =>
      reactQueryPost(
        internalApi.embeds.create({ params: { content: embedUrl } }),
      ),
    {
      onSuccess: data => {
        if (!data.html) {
          toast.error(t("courses.edit.lesson.embed_link_is_not_embeddable"));
          return;
        }
        setValue("featured_media_embed", data, {
          shouldDirty: true,
        });
        void mediaPickerModal.hide();
      },
    },
  );
  const handleOnUploadStarted = () => {
    setValue("featured_media", null, {
      shouldDirty: true,
    });
  };

  const handleOnUploadComplete = (file: any) => {
    const uploadedMediaUrl = activeStorageBlobUrl({
      signed_id: file.signed_id,
      filename: file.filename,
    });
    setValue(
      "featured_media",
      {
        ...file,
        url: uploadedMediaUrl,
        playback_url: uploadedMediaUrl,
      },
      {
        shouldDirty: true,
      },
    );
  };

  const showPlayback =
    isFeaturedMediaEnabled && (hasPlaybackUrl || hasEmbeddedMedia);
  const showPicker =
    isFeaturedMediaEnabled &&
    !hasPlaybackUrl &&
    !hasEmbeddedMedia &&
    !createEmbedMutation.isLoading;

  return (
    <>
      {showPlayback && hasPlaybackUrl && (
        <MediaWithPlayback
          featuredMedia={featuredMedia}
          isAudioMode={isAudioMode}
          onRemove={() =>
            setValue("featured_media", null, {
              shouldDirty: true,
            })
          }
        />
      )}
      {showPlayback && featuredMediaEmbed && (
        <EmbedPreview
          featuredMediaEmbed={featuredMediaEmbed}
          setValue={setValue}
        />
      )}
      {createEmbedMutation.isLoading && <EmbedLoading />}
      <div className={classNames({ hidden: !showPicker })}>
        <MediaPicker
          createEmbed={createEmbedMutation.mutate}
          onUploadStarted={handleOnUploadStarted}
          onUploadComplete={handleOnUploadComplete}
        />
      </div>
      <FormTipTapBlockEditor
        placeholder={t("courses.edit.lesson.placeholder")}
        name="rich_text_body.body"
        editorClassName="min-h-[500px]"
        sgidToObjectMap={lesson.rich_text_body.sgids_to_object_map || {}}
        inlineAttachments={lesson.rich_text_body.inline_attachments || []}
        disabledExtensions={["mention", "poll"]}
        aiCopilotTitle={name}
      />
    </>
  );
};
